import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import RootStore from '@App/stores/root-store'
import { PaginationBody, PaginationInfo } from '@Entities/pagination'
import { WorkModel } from '@Entities/work/work'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { omit } from 'lodash'
import { findPageOnLimitChange } from '@App/helpers/pagination/find-page-on-limit-change'
import SearchCriteriaStore from '@App/stores/search/search-criteria-store'
import { SearchOptionType } from '@App/enum/search-option-type'
import { RequiredCriteria } from '@App/enum/required-criteria'
import { SearchCriterion } from '@App/stores/search/models/search-criterion'
import { SidebarDownloadType } from '@App/enum/sidebar-download-type'
import { isEmptyObject, isNotAnEmptyObject } from '@App/helpers/check-is-empty-object'
import { WorkSearch } from '@App/stores/search/models/work-search'
import { searchWorksRH } from '@App/api/routes/search'
import { SongwriterCatalogFilterType } from '@Scenes/SongwriterScene/SongwriterCatalogScene'
import { Works } from '@Entities/work/works'
import { SelectOptionHide } from '@App/stores/search/models/select-option-hide'
import { v4 as uuidv4 } from 'uuid'
import getWorkAutoCompleteSuggestions from '@App/helpers/search/get-work-autocomplete-suggestions'
import getWriterNameAutoCompleteSuggestions from '@App/helpers/search/get-writer-name-auto-complete-suggestions'
import getPublisherNameAutoCompleteSuggestions from '@App/helpers/search/get-publisher-name-auto-complete-suggestions'
import {
    createMatchedRecordingSearchOptions,
    MATCHED_RECORDING_NON_EXPORTABLE_KEYS,
} from '@App/stores/search/work/matched-recording-search-options'
import {
    catalogFilterTransformers,
    RightsholderCatalogSearchFilter,
} from '@App/forms/filters/rightsholder-catalog-filter'
import { ALPHABETICAL_WORK_TITLE_SORT } from '@App/const/pagination'
import { mapCriteriaToObject } from '@App/helpers/map-criteria-to-object'

export const defaultCatalogPagination = { page: 1, limit: 10 }

interface CatalogSearchFilter {
    holdTypeId?: number[]
    matchedRecordingsFilter?: string
    hasUnclaimedShare?: boolean
}

export const SEARCH_CATALOG_WORK_OPTIONS: Array<SelectOptionHide<WorkSearch>> = [
    {
        id: uuidv4(),
        value: 'combinedTitles',
        label: 'Work Title',
        isPrimary: true,
        autoCompleteFetcher: ({ searchTerm, rightsholderId, songwriterId, signal }) =>
            getWorkAutoCompleteSuggestions({
                property: 'combinedTitles',
                searchTerm,
                rightsholderId,
                songwriterId,
                signal,
            }),
        exactMatchOnly: true,
    },
    {
        id: uuidv4(),
        value: 'writerFullNames',
        label: 'Writer Name',
        autoCompleteFetcher: getWriterNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'writerNameNumbers', label: 'Writer IPI' },
    { id: uuidv4(), value: 'iswc', label: 'ISWC', isPrimary: true },
    { id: uuidv4(), value: 'hfaSongCode', label: 'MLC Song Code', isPrimary: true },
    {
        id: uuidv4(),
        value: 'originalPubNames',
        label: 'Publisher Name',
        autoCompleteFetcher: getPublisherNameAutoCompleteSuggestions,
        exactMatchOnly: true,
    },
    { id: uuidv4(), value: 'originalPubIpiNameNumbers', label: 'Publisher IPI' },
    { id: uuidv4(), value: 'bothPublisherNumbers', label: 'MLC Publisher Number' },
    { id: uuidv4(), value: 'memberProprietaryId', label: 'Member Work Identifier', isPrimary: true, isUnique: true },
    { id: uuidv4(), value: 'matchedRecordingArtists', label: 'Artist' },
    ...createMatchedRecordingSearchOptions(),
]
export const defaultCatalogSelectionCriteria: Array<SearchCriterion<WorkSearch>> = [
    {
        id: SEARCH_CATALOG_WORK_OPTIONS[0].id,
        selectedOption: SEARCH_CATALOG_WORK_OPTIONS[0],
        searchTerm: '',
        isFixed: true,
    },
]

const nonExportableKeys: Array<keyof WorkSearch | keyof CatalogSearchFilter> = [
    ...MATCHED_RECORDING_NON_EXPORTABLE_KEYS,
    'holdTypeId',
]

export default class CatalogSearchStore {
    protected rootStore: RootStore
    constructor(rootStore: RootStore) {
        makeObservable<
            CatalogSearchStore,
            | '_worksRightsHolder'
            | '_worksRightsHolderPaginationInfo'
            | '_pagination'
            | '_isSearchExportable'
            | '_currentSearchFilters'
        >(this, {
            _worksRightsHolder: observable,
            _worksRightsHolderPaginationInfo: observable,
            _pagination: observable,
            _isSearchExportable: observable,
            _currentSearchFilters: observable,
            emptyResults: computed,
            setCurrentPaginationPage: action,
            setPagination: action,
            applyLimitChange: action,
            applyResults: action,
            applySearchResults: action,
            setCurrentSearchFilters: action,
            seedSearchFromUrl: action,
            destroyPagination: action,
            destroyCurrentSearchFilter: action,
            destroyWorksRightsHolderPaginationInfo: action,
            destroyWorksRightsHolder: action,
            destroyCatalogCriteria: action,
            destroyCurrentPage: action,
        })

        this.rootStore = rootStore
        this.resetSearchCriteriaStore()
    }

    protected _worksRightsHolder: Works | undefined

    protected _worksRightsHolderPaginationInfo: PaginationInfo | undefined

    searchCriteriaStore: SearchCriteriaStore<WorkSearch>

    private _pagination: PaginationBody = { ...defaultCatalogPagination }

    private _isSearchExportable: boolean

    private _catalogSelectionQuery: WorkSearch | undefined

    private _currentSearchFilters: CatalogSearchFilter = {}

    get pagination(): PaginationBody {
        return this._pagination
    }

    get isSearchExportable(): boolean {
        return this._isSearchExportable
    }

    get limit(): number {
        return this._pagination.limit ? this._pagination.limit : 10
    }

    get emptyResults(): boolean {
        return !this._worksRightsHolder?.works?.length
    }

    get worksRightsHolder(): Works | undefined {
        return this._worksRightsHolder
    }

    get worksRightsHolderPaginationInfo(): PaginationInfo | undefined {
        return this._worksRightsHolderPaginationInfo
    }

    get catalogSelectionSearchQuery(): WorkSearch | undefined {
        return this._catalogSelectionQuery
    }

    /** The full query sent to the API: the selected criteria merged with the applied filters. */
    get searchQuery(): WorkSearch {
        return {
            ...this._catalogSelectionQuery,
            ...this._currentSearchFilters,
        }
    }

    get sidebarDownloadType(): SidebarDownloadType {
        return SidebarDownloadType.CatalogExport
    }

    /**
     * Identifies the data set this store searches. Used as part of the cache key so results for
     * different rightsholders/songwriters are never shared.
     */
    get searchScopeKey(): unknown[] {
        return ['rightsholder-catalog', this.rootStore.rightsholderStore.currentRightsholderId]
    }

    public reportSearchError(error: unknown): void {
        this.rootStore.popupStore.setAlertFromError(error)
    }

    public setCurrentSearchFilters(filters: Partial<CatalogSearchFilter | SongwriterCatalogFilterType>) {
        this._currentSearchFilters = this.getCatalogSearchFilter(filters)
    }

    public setCurrentPaginationPage(page: number) {
        this._pagination.page = page
    }

    public setPagination(pagination: PaginationBody) {
        this._pagination = { ...pagination }
    }

    /** Moves to a new results-per-page value, keeping the user on the page holding their first result. */
    public applyLimitChange(newLimit: number) {
        const newPage = findPageOnLimitChange(
            this._pagination,
            this._worksRightsHolder ? this._worksRightsHolder.count : 1,
            newLimit
        )
        this.setPagination({ page: newPage, limit: newLimit })
    }

    setCatalogSelectionSearchQuery(catalogSelectionQuery: WorkSearch): void {
        this._catalogSelectionQuery = catalogSelectionQuery
    }

    /**
     * Starts a visit from a link's search terms and filters. Run it before the page first renders: it
     * swaps in a criteria store seeded from the URL (not merged with rows left from an earlier visit),
     * drops results fetched for a different search, and — only if the terms satisfy the form's own
     * "ready to search" rule — makes them the search, so the first request already carries everything.
     */
    public seedSearchFromUrl(criteria: Record<string, unknown>, filters?: Partial<CatalogSearchFilter>) {
        this.resetSearchCriteriaStore()
        this.searchCriteriaStore.applyCriteriaFromObject(criteria)
        this.destroyCatalogCriteria()
        this.setCurrentSearchFilters(filters ?? {})
        const rows = this.searchCriteriaStore.searchCriteria
        if (this.searchCriteriaStore.isCriteriaSearchReady(rows, filters)) {
            this.setCatalogSelectionSearchQuery(mapCriteriaToObject(rows))
        }
    }

    public resetSearchCriteriaStore() {
        const maxCriteria = 8
        this.searchCriteriaStore = new SearchCriteriaStore(
            defaultCatalogSelectionCriteria,
            SEARCH_CATALOG_WORK_OPTIONS,
            SearchOptionType.primary,
            RequiredCriteria.primaryFilter,
            maxCriteria,
            ['MLC_OVERCLAIM', 'MLC_DISPUTE']
        )
    }

    public resetStore() {
        this.resetSearchCriteriaStore()
        this.destroyWorksRightsHolderPaginationInfo()
        this.destroyWorksRightsHolder()
        this.destroyPagination()
        this.destroyCatalogCriteria()
    }

    async getWorksRightsHolder(pagination: PaginationBody, search?: WorkSearch): Promise<void> {
        try {
            const response = await this.fetchWorks(pagination, search)
            this.applyResults(response, pagination)
        } catch (e) {
            this.rootStore.popupStore.setAlertFromError(e)
        }
    }

    /**
     * Performs the search request without touching any observable state, so it can be owned by an
     * external cache (TanStack Query). Subclasses override this to hit a different endpoint.
     */
    public async fetchWorks(pagination: PaginationBody, search?: WorkSearch): Promise<Works> {
        return this.getWorksRightsHolderApiCall(
            this.rootStore.rightsholderStore.currentRightsholderId,
            pagination,
            search
        )
    }

    /** Stores results fetched elsewhere so components still reading the store stay in sync. */
    public applyResults(response: Works, pagination: PaginationBody): void {
        runInAction(() => {
            this._worksRightsHolder = response
            if (pagination.limit && pagination.page && response) {
                const data = calcNumPages(response.count, pagination.limit, pagination.page)
                if (!data) {
                    return
                }
                this._worksRightsHolderPaginationInfo = data
            }
        })
        this.loadRelatedData()
    }

    /**
     * Stores results for the store's current pagination and refreshes the derived search state
     * that `searchForWorks` would normally have set.
     */
    public applySearchResults(response: Works): void {
        this.applyResults(response, this._pagination)
        this.setCurrentPaginationPage(this._pagination.page ?? 1)
        runInAction(() => {
            this._isSearchExportable = this.canSearchTermBeExported()
        })
    }

    /** Extra data that has to be loaded alongside a page of results. */
    protected loadRelatedData(): void {
        this.fetchClaimedShares()
    }

    private async getWorksRightsHolderApiCall(rightsHolderId: number, pagination: PaginationBody, search?: WorkSearch) {
        // get info with search
        // Only fall back to the default sort when the caller hasn't already selected one explicitly,
        // otherwise an empty search would silently override e.g. a "Most Recent" pagination sort.
        if (!pagination.sort && (!search || isEmptyObject(omit(search, 'holdTypeId')))) {
            pagination.sort = ALPHABETICAL_WORK_TITLE_SORT
        }

        const searchResponse = await searchWorksRH(rightsHolderId, pagination, search)
        return {
            count: searchResponse.data.totalElements,
            works: searchResponse.data.content,
            error: undefined,
        }
    }

    private fetchClaimedShares = () => {
        if (!this.emptyResults && this.worksRightsHolder) {
            // If the above computed value is true, then worksRightsHolder.works exists.
            const workIds = this.worksRightsHolder.works
                .map((work: WorkModel) => work.id)
                .filter((workId: number | null | undefined): workId is number => !!workId)
            this.rootStore.rightsholderStore.loadClaimedShares(workIds).then()
        }
    }

    private getCatalogSearchFilter = (
        filters: Partial<RightsholderCatalogSearchFilter | SongwriterCatalogFilterType>
    ): CatalogSearchFilter => {
        const catalogSearchFilter: CatalogSearchFilter = {}
        Object.entries(filters).forEach(([key, value]) => {
            // isNotAnEmptyObject checks Object.keys(value).length, which is always 0 for a boolean
            const isSet = typeof value === 'boolean' ? value : isNotAnEmptyObject(value)
            if (isSet) {
                const transformer = catalogFilterTransformers[key as keyof RightsholderCatalogSearchFilter]
                Object.assign(catalogSearchFilter, transformer ? transformer(value) : { [key]: value })
            }
        })

        return catalogSearchFilter
    }

    /** Check if searchFilterValue is a field that cannot be exported and see if singleSearchValue is populated */
    private canSearchTermBeExported = (): boolean => {
        if (this.emptyResults) {
            return false
        }
        if (!this._catalogSelectionQuery) {
            return true
        }

        return ![...Object.entries(this._catalogSelectionQuery), ...Object.entries(this._currentSearchFilters)]
            .filter(([, value]) => !!value)
            .filter(([, value]) => !(Array.isArray(value) && value.length === 0))
            .map(([key]) => key)
            .some((key) => nonExportableKeys.includes(key as keyof WorkSearch))
    }
    destroyWorksRightsHolderPaginationInfo(): void {
        this._worksRightsHolderPaginationInfo = undefined
    }
    destroyWorksRightsHolder(): void {
        this._worksRightsHolder = undefined
    }
    destroyPagination(): void {
        this._pagination = { ...defaultCatalogPagination }
    }
    destroyCurrentPage() {
        this._pagination.page = 1
    }
    destroyCatalogCriteria(): void {
        this._pagination = { ...defaultCatalogPagination }
        this._catalogSelectionQuery = {}
        this._worksRightsHolder = undefined
        this._worksRightsHolderPaginationInfo = undefined
    }

    destroyCurrentSearchFilter(): void {
        this._currentSearchFilters = {}
    }
}
